import { Fragment, useMemo, useState } from "react";
import { useMonthlyReturns } from "@/hooks/useData";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtMoney, fmtSignedMoney, fmtPct, fmtNum, pnlClass } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const tint = (v) => (v == null ? "" : v > 0 ? "bg-[hsl(var(--profit))]/12" : v < 0 ? "bg-[hsl(var(--loss))]/12" : "bg-surface-2");
const signedPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(2)}%`);
const iso = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-[10px] text-muted-foreground">
      {[["bg-[hsl(var(--profit))]/25", "Profit"], ["bg-[hsl(var(--loss))]/25", "Loss"], ["bg-surface-2", "No activity"]].map(([c, l]) => (
        <span key={l} className="flex items-center gap-1.5">
          <span className={cn("h-2.5 w-2.5 rounded-sm", c)} /> {l}
        </span>
      ))}
      <span>Percentages are compounded against the balance entering each period.</span>
    </div>
  );
}

function PeriodCard({ label, data, currency, testId }) {
  const d = data || {};
  return (
    <div className="rounded-xl border bg-card p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-1 font-display text-lg font-semibold tabular-nums", pnlClass(d.profit))}>
        {fmtSignedMoney(d.profit, currency)}
      </div>
      <div className={cn("text-xs font-medium tabular-nums", pnlClass(d.gain_pct))}>{signedPct(d.gain_pct)}</div>
      <div className="mt-2 border-t pt-2 text-[10px] text-muted-foreground">
        {fmtNum(d.trades)} trades · {fmtPct(d.win_rate)} win · {d.lots ?? 0} lots
      </div>
    </div>
  );
}

function TableView({ years, currency, onSelectMonth }) {
  if (!years?.length) return <p className="py-10 text-center text-sm text-muted-foreground">No closed trades yet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="monthly-returns-table-view">
      <table className="w-full min-w-[860px] border-separate border-spacing-0.5 text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="p-1 text-left">Year</th>
            {MONTHS.map((m) => <th key={m} className="p-1 text-center font-medium">{m}</th>)}
            <th className="p-1 text-center font-semibold text-foreground">Year</th>
          </tr>
        </thead>
        <tbody>
          {years.map((y) => (
            <tr key={y.year} data-testid={`monthly-returns-row-${y.year}`}>
              <td className="p-1 text-left font-display text-sm font-semibold">{y.year}</td>
              {y.months.map((m) => (
                <td key={m.month} onClick={() => m.gain_pct != null && onSelectMonth(y.year, m.month)}
                  className={cn("rounded-md p-1.5 text-center align-middle", tint(m.gain_pct),
                    m.gain_pct != null && "cursor-pointer transition-transform hover:scale-105")}
                  data-testid={`monthly-cell-${y.year}-${m.month}`}>
                  <div className={cn("text-[11px] font-semibold tabular-nums", m.gain_pct == null ? "text-muted-foreground" : pnlClass(m.gain_pct))}>
                    {signedPct(m.gain_pct)}
                  </div>
                  {m.profit != null && (
                    <div className="text-[9px] tabular-nums text-muted-foreground">{fmtSignedMoney(m.profit, currency, true)}</div>
                  )}
                </td>
              ))}
              <td className={cn("rounded-md p-1.5 text-center align-middle", tint(y.total_gain_pct))}>
                <div className={cn("text-[11px] font-bold tabular-nums", pnlClass(y.total_gain_pct))}>{signedPct(y.total_gain_pct)}</div>
                <div className="text-[9px] tabular-nums text-muted-foreground">{fmtSignedMoney(y.total_profit, currency, true)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CalendarView({ data, currency, onSelectMonth }) {
  const monthKeys = useMemo(() => Object.keys(data.months || {}).sort(), [data.months]);
  const [cursor, setCursor] = useState(monthKeys[monthKeys.length - 1] || new Date().toISOString().slice(0, 7));
  const [year, month] = cursor.split("-").map(Number);
  const entry = data.months?.[cursor];
  const days = data.days || {};

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setCursor(`${y}-${String(m).padStart(2, "0")}`);
  };

  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const total = new Date(year, month, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: total }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, i) => cells.slice(i * 7, i * 7 + 7));

  return (
    <div data-testid="monthly-returns-calendar-view">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => shift(-1)} className="rounded-md border p-1 hover:bg-surface-2"
            data-testid="monthly-calendar-prev">
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <div className="font-display text-base font-semibold" data-testid="monthly-calendar-title">{MONTHS[month - 1]} {year}</div>
          <button type="button" onClick={() => shift(1)} className="rounded-md border p-1 hover:bg-surface-2"
            data-testid="monthly-calendar-next">
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => onSelectMonth(year, month)}
            className="ml-1 rounded-full border border-dashed px-2 py-0.5 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
            data-testid="monthly-calendar-view-in-equity">
            View in equity curve
          </button>
        </div>
        <div className="flex items-center gap-5 text-xs">
          <span className={cn("font-semibold tabular-nums", pnlClass(entry?.profit))}>
            {entry ? fmtSignedMoney(entry.profit, currency) : "No trades"}
          </span>
          <span className={cn("font-semibold tabular-nums", pnlClass(entry?.gain_pct))}>{entry ? signedPct(entry.gain_pct) : "—"}</span>
          <span className="text-muted-foreground">{entry?.trades || 0} trades · {entry?.traded_days || 0} traded days</span>
        </div>
      </div>

      <div className="grid grid-cols-8 gap-1">
        {WEEKDAYS.map((d) => <div key={d} className="pb-1 text-center text-[10px] uppercase tracking-wider text-muted-foreground">{d}</div>)}
        <div className="pb-1 text-center text-[10px] uppercase tracking-wider text-muted-foreground">Week</div>
        {weeks.map((w, wi) => {
          const wDays = w.filter(Boolean).map((d) => days[iso(year, month, d)]).filter(Boolean);
          const wProfit = wDays.reduce((a, d) => a + d.profit, 0);
          const wTrades = wDays.reduce((a, d) => a + d.trades, 0);
          return (
            <Fragment key={wi}>
              {w.map((d, di) => {
                const e = d ? days[iso(year, month, d)] : null;
                return (
                  <div key={`${wi}-${di}`} onClick={() => d && e && onSelectMonth(year, month)}
                    className={cn("flex min-h-[62px] flex-col rounded-lg border p-1.5", d ? tint(e?.profit ?? null) : "border-transparent",
                      d && e && "cursor-pointer transition-transform hover:scale-105")}
                    data-testid={d ? `monthly-day-${iso(year, month, d)}` : undefined}>
                    {d && <span className="text-[9px] text-muted-foreground">{d}</span>}
                    {e && (
                      <div className="mt-auto">
                        <div className={cn("text-[11px] font-semibold tabular-nums", pnlClass(e.profit))}>
                          {fmtSignedMoney(e.profit, currency, true)}
                        </div>
                        <div className={cn("text-[9px] tabular-nums", pnlClass(e.gain_pct))}>{signedPct(e.gain_pct)}</div>
                        <div className="text-[9px] text-muted-foreground">{e.trades}t</div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div key={`${wi}-total`} className={cn("flex min-h-[62px] flex-col justify-center rounded-lg border border-dashed p-1.5 text-center",
                wDays.length ? tint(wProfit) : "")} data-testid={`monthly-week-total-${wi}`}>
                {wDays.length ? (
                  <>
                    <div className={cn("text-[11px] font-semibold tabular-nums", pnlClass(wProfit))}>
                      {fmtSignedMoney(wProfit, currency, true)}
                    </div>
                    <div className="text-[9px] text-muted-foreground">{wTrades}t · {wDays.length}d</div>
                  </>
                ) : <span className="text-[9px] text-muted-foreground">—</span>}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function SummaryView({ data, currency }) {
  const t = data.totals || {};
  const weeks = (data.weeks || []).slice(0, 12);
  return (
    <div className="space-y-4" data-testid="monthly-returns-summary-view">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <PeriodCard label="Today" data={data.periods?.today} currency={currency} testId="period-card-today" />
        <PeriodCard label="This week" data={data.periods?.week} currency={currency} testId="period-card-week" />
        <PeriodCard label="This month" data={data.periods?.month} currency={currency} testId="period-card-month" />
        <PeriodCard label="This year" data={data.periods?.year} currency={currency} testId="period-card-year" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-4">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Last 12 weeks</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-muted-foreground">
                <th className="pb-2">Week of</th><th className="pb-2 text-right">P/L</th>
                <th className="pb-2 text-right">%</th><th className="pb-2 text-right">Trades</th><th className="pb-2 text-right">Days</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.week_start} className="border-t" data-testid={`week-row-${w.week_start}`}>
                  <td className="py-1.5">{w.week_start}</td>
                  <td className={cn("py-1.5 text-right font-medium tabular-nums", pnlClass(w.profit))}>{fmtSignedMoney(w.profit, currency)}</td>
                  <td className={cn("py-1.5 text-right tabular-nums", pnlClass(w.gain_pct))}>{signedPct(w.gain_pct)}</td>
                  <td className="py-1.5 text-right tabular-nums">{w.trades}</td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">{w.traded_days}</td>
                </tr>
              ))}
              {!weeks.length && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No trading weeks yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border bg-card p-4">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Highlights</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
            {[
              ["Traded days", fmtNum(t.traded_days)],
              ["Traded months", fmtNum(t.traded_months)],
              ["Avg daily", signedPct(t.avg_daily_pct)],
              ["Avg monthly", signedPct(t.avg_monthly_pct)],
              ["Positive months", `${t.positive_months || 0}`],
              ["Negative months", `${t.negative_months || 0}`],
              ["Best day", t.best_day ? `${fmtSignedMoney(t.best_day.profit, currency)} · ${t.best_day.date}` : "—"],
              ["Worst day", t.worst_day ? `${fmtSignedMoney(t.worst_day.profit, currency)} · ${t.worst_day.date}` : "—"],
              ["Best month", t.best_month ? `${fmtSignedMoney(t.best_month.profit, currency)} · ${t.best_month.month}` : "—"],
              ["Worst month", t.worst_month ? `${fmtSignedMoney(t.worst_month.profit, currency)} · ${t.worst_month.month}` : "—"],
              ["Starting balance", fmtMoney(data.start_balance, currency)],
            ].map(([label, value]) => (
              <div key={label}>
                <div className="font-semibold tabular-nums">{value}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MonthlyReturnsPanel({ accountId, currency = "USD", symbol = "ALL", onSelectMonth, shareToken }) {
  const { data, isLoading } = useMonthlyReturns(accountId, symbol, shareToken);
  const selectMonth = shareToken ? undefined : onSelectMonth;

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-5" data-testid="monthly-returns-panel">
      <div className="mb-4">
        <div className="font-display text-sm font-semibold">Monthly returns</div>
        <div className="text-[11px] text-muted-foreground">
          Every month of this account's history in currency and percent, with daily and weekly detail.
          Click any month or day to view it on the equity curve above.
        </div>
      </div>

      {isLoading || !data ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : (
        <Tabs defaultValue="table" className="space-y-4">
          <TabsList data-testid="monthly-returns-tabs">
            <TabsTrigger value="table" data-testid="monthly-returns-tab-table">Table</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="monthly-returns-tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="summary" data-testid="monthly-returns-tab-summary">Summary</TabsTrigger>
          </TabsList>
          <TabsContent value="table" className="space-y-3">
            <TableView years={data.years} currency={currency} onSelectMonth={selectMonth} />
            <Legend />
          </TabsContent>
          <TabsContent value="calendar" className="space-y-3">
            <CalendarView data={data} currency={currency} onSelectMonth={selectMonth} />
            <Legend />
          </TabsContent>
          <TabsContent value="summary">
            <SummaryView data={data} currency={currency} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
